import { Routes } from '@angular/router';
import { AuthGuard } from '../../guard/auth-guard/auth.guard';
import { RoleGuard } from '../../guard/auth-guard/role.guard';
import { Role } from '../../core/enums/role.enum';
import { alteracoesNaoSalvasGuard } from '../../core/guards/alteracoes-nao-salvas.guard';
import { saidaLoteGuard } from '../../core/guards/saida-lote.guard';

/** Item 2.1 da HU — qualquer permissão da funcionalidade dá acesso à tela inicial. */
const TODAS_PERMISSOES = [
  Role.FOLHA_PAGAMENTO_VISUALIZAR,
  Role.FOLHA_PAGAMENTO_ADD_COMPETENCIA,
  Role.FOLHA_PAGAMENTO_EDITAR,
  Role.FOLHA_PAGAMENTO_CADASTRAR_LOTE,
  Role.FOLHA_PAGAMENTO_RELATORIO_GERAL,
  Role.FOLHA_PAGAMENTO_EXCLUIR_LOTE,
  Role.FOLHA_PAGAMENTO_PROCESSAR_RETORNO,
  Role.FOLHA_PAGAMENTO_CONCLUIR_LOTE,
  Role.FOLHA_PAGAMENTO_RELATORIO_SIAPE,
];

/**
 * Leitura da folha — as duas telas que exibem a grid: Visualizar Folha (*apenas* a
 * permissão Visualizar) e Editar Folha.
 *
 * Cadastrar Lote não entra: o Passo 2 do lote tem tela e rota próprias.
 */
const LEITURA_FOLHA = [Role.FOLHA_PAGAMENTO_VISUALIZAR, Role.FOLHA_PAGAMENTO_EDITAR];

/** Tela do lote: quem opera o retorno e a conclusão também precisa vê-la. */
const LEITURA_LOTE = [
  Role.FOLHA_PAGAMENTO_VISUALIZAR,
  Role.FOLHA_PAGAMENTO_EDITAR,
  Role.FOLHA_PAGAMENTO_CADASTRAR_LOTE,
  Role.FOLHA_PAGAMENTO_PROCESSAR_RETORNO,
  Role.FOLHA_PAGAMENTO_CONCLUIR_LOTE,
  Role.FOLHA_PAGAMENTO_EXCLUIR_LOTE,
];

const BASE = 'pagamento/gerenciar-folha-pagamento';
const BREADCRUMB_BASE = [
  { label: 'Pagamento', url: '/pagamento' },
  { label: 'Gerenciar Folha de Pagamento', url: `/${BASE}` },
];

export const gerenciarFolhaPagamentoRoutes: Routes = [
  {
    path: BASE,
    canActivate: [AuthGuard, RoleGuard],
    data: {
      title: 'Gerenciar Folha de Pagamento',
      roles: TODAS_PERMISSOES,
      breadcrumb: BREADCRUMB_BASE,
    },
    loadComponent: () =>
      import('./competencias/competencias.component').then((m) => m.CompetenciasComponent),
  },
  {
    // Edição da folha. A tela abre para quem tem permissão de editar;
    // o bloqueio por profissional (Pago ou Em processamento) é aplicado linha a linha.
    path: `${BASE}/competencias/:competenciaId/editar`,
    canActivate: [AuthGuard, RoleGuard],
    data: {
      // O banner recebe a competência depois do carregamento (PageTitleService).
      title: 'Editar folha',
      roles: [Role.FOLHA_PAGAMENTO_EDITAR],
      breadcrumb: [...BREADCRUMB_BASE, { label: 'Editar Folha' }],
    },
    loadComponent: () =>
      import('./folha/folha.component').then((m) => m.FolhaComponent),
  },
  {
    // Visualização da folha — mesma tela, sem ações de edição e com os mesmos filtros.
    path: `${BASE}/competencias/:competenciaId/visualizar`,
    canActivate: [AuthGuard, RoleGuard],
    data: {
      title: 'Visualizar folha',
      roles: LEITURA_FOLHA,
      breadcrumb: [...BREADCRUMB_BASE, { label: 'Visualizar Folha' }],
      somenteLeitura: true,
    },
    loadComponent: () =>
      import('./folha/folha.component').then((m) => m.FolhaComponent),
  },
  {
    // Histórico de alterações da competência. Aberto pelo botão
    // "Visualizar todas as alterações" das telas de folha.
    path: `${BASE}/competencias/:competenciaId/alteracoes`,
    canActivate: [AuthGuard, RoleGuard],
    data: {
      // O banner recebe a competência depois do carregamento (PageTitleService).
      title: 'Histórico de alterações',
      roles: LEITURA_FOLHA,
      breadcrumb: [...BREADCRUMB_BASE, { label: 'Histórico de Alterações' }],
    },
    loadComponent: () =>
      import('./historico-alteracoes/historico-alteracoes.component').then(
        (m) => m.HistoricoAlteracoesComponent,
      ),
  },
  {
    // Protótipo 06 — folha individual. O guard de alterações não salvas dispara MS024.
    // Só para quem edita: a consulta tem rota própria, logo abaixo (PGPP-615, B-01).
    path: `${BASE}/profissionais/:folhaProfissionalId`,
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [alteracoesNaoSalvasGuard],
    data: {
      title: 'Editar Dados do Profissional',
      roles: [Role.FOLHA_PAGAMENTO_EDITAR],
      breadcrumb: [...BREADCRUMB_BASE, { label: 'Folha do Profissional' }],
    },
    loadComponent: () =>
      import('./folha-profissional/folha-profissional.component').then(
        (m) => m.FolhaProfissionalComponent,
      ),
  },
  {
    // Protótipo 03 — "Visualizar" o profissional: a mesma tela, em somente leitura, para as
    // telas Visualizar Folha e Editar Folha (PGPP-615, B-01 e S-05). Sem guard: nada a perder.
    path: `${BASE}/profissionais/:folhaProfissionalId/visualizar`,
    canActivate: [AuthGuard, RoleGuard],
    data: {
      title: 'Visualizar Dados do Profissional',
      roles: LEITURA_FOLHA,
      breadcrumb: [...BREADCRUMB_BASE, { label: 'Folha do Profissional' }],
      somenteLeitura: true,
    },
    loadComponent: () =>
      import('./folha-profissional/folha-profissional.component').then(
        (m) => m.FolhaProfissionalComponent,
      ),
  },
  {
    // Protótipo 04 — cadastro de lote em três passos.
    path: `${BASE}/competencias/:competenciaId/lotes/cadastrar`,
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [alteracoesNaoSalvasGuard],
    data: {
      title: 'Cadastrar novo lote',
      roles: [Role.FOLHA_PAGAMENTO_CADASTRAR_LOTE],
      breadcrumb: [...BREADCRUMB_BASE, { label: 'Cadastrar Lote' }],
    },
    loadComponent: () =>
      import('./lote-cadastro/lote-cadastro.component').then((m) => m.LoteCadastroComponent),
  },
  {
    // Tela do lote, com upload e processamento do retorno.
    // canDeactivate dispara MS010 em qualquer saída, não só no botão Voltar.
    path: `${BASE}/lotes/:loteId`,
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [saidaLoteGuard],
    data: {
      title: 'Visualizar lote',
      roles: LEITURA_LOTE,
      breadcrumb: [...BREADCRUMB_BASE, { label: 'Visualizar Lote' }],
    },
    loadComponent: () =>
      import('./lote-detalhe/lote-detalhe.component').then((m) => m.LoteDetalheComponent),
  },
  {
    // Protótipo 07 — Relatório Geral.
    path: `${BASE}/competencias/:competenciaId/relatorio`,
    canActivate: [AuthGuard, RoleGuard],
    data: {
      title: 'Relatório geral',
      roles: [Role.FOLHA_PAGAMENTO_RELATORIO_GERAL],
      breadcrumb: [...BREADCRUMB_BASE, { label: 'Relatório' }],
    },
    loadComponent: () =>
      import('./relatorio/relatorio.component').then((m) => m.RelatorioComponent),
  },
];
